import React from "react";
import { File as FileIcon, X, CircleCheck, CircleAlert } from "lucide-react";
import { Button } from "../../atoms/Button/Button.js";
import { Progress } from "../../atoms/Progress/Progress.js";
import { formatBytes } from "./file-rules.js";
import type { UploadFile } from "./FileUpload.js";

/* FileUpload's file list (internal, not in the barrel), moved out of
   FileUpload.tsx with its markup and classes unchanged. The remove control,
   named "Remove <file name>", is the design-system Button: ghost, iconOnly,
   xs (24px, 8px corner, the one focus-ring; SN-12, PR 3). */

interface FileUploadListProps {
  files: UploadFile[];
  disabled?: boolean | undefined;
  onRemove: (id: string) => void;
}

export function FileUploadList({
  files,
  disabled,
  onRemove,
}: FileUploadListProps): React.ReactElement | null {
  if (files.length === 0) return null;
  return (
    <ul className="mt-2 flex flex-col gap-1.5">
      {files.map((uf) => (
        <li
          key={uf.id}
          className="flex items-center gap-2 rounded-md border border-pb-border bg-pb-card px-2.5 py-2"
        >
          <FileIcon
            className="w-4 h-4 shrink-0 text-pb-text-muted"
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm text-pb-text">
                {uf.file.name}
              </span>
              <span className="text-dense-1 text-pb-text-muted pb-tabular shrink-0">
                {formatBytes(uf.file.size)}
              </span>
            </div>
            {uf.status === "uploading" && (
              <Progress value={uf.progress ?? 0} className="mt-1" />
            )}
            {uf.status === "error" && uf.error && (
              <p className="text-dense-1 text-pb-red-text mt-0.5">{uf.error}</p>
            )}
          </div>
          {uf.status === "success" && (
            <CircleCheck
              className="w-4 h-4 shrink-0 text-pb-teal-text"
              aria-label="Uploaded"
            />
          )}
          {uf.status === "error" && (
            <CircleAlert
              className="w-4 h-4 shrink-0 text-pb-red-text"
              aria-label="Failed"
            />
          )}
          <Button
            variant="ghost"
            iconOnly
            size="xs"
            aria-label={`Remove ${uf.file.name}`}
            disabled={disabled}
            onClick={() => onRemove(uf.id)}
            className="shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </li>
      ))}
    </ul>
  );
}
